import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';

// Mot de passe trop faible : details = liste de chaînes (validatePasswordStrength), pas [{ message }]
const passwordErrorMessage = (err: unknown): string => {
  const details = (err as { response?: { data?: { error?: { details?: unknown } } } })?.response?.data?.error?.details;
  if (Array.isArray(details) && details.every((d) => typeof d === 'string')) return details.join(' · ');
  return apiErrorMessage(err, 'Changement du mot de passe impossible');
};

const EMPTY = { currentPassword: '', newPassword: '', confirm: '' };

interface Props { onDone: () => void }

/** Changement de son propre mot de passe : l'ancien est vérifié par l'API, la confirmation ici. */
export function PasswordChangeForm({ onDone }: Props) {
  const [form, setForm] = useState(EMPTY);
  const [mismatch, setMismatch] = useState(false);

  const change = useMutation({
    mutationFn: async () => {
      await api.patch('/users/me/password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
    },
    onSuccess: () => {
      toast.success('Mot de passe modifié');
      setForm(EMPTY);
      onDone();
    },
    onError: (err) => toast.error(passwordErrorMessage(err)),
  });

  const submit = () => {
    if (form.newPassword !== form.confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate();
  };

  return (
    <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <div className="space-y-1">
        <Label htmlFor="pwd-current">Mot de passe actuel</Label>
        <PasswordInput id="pwd-current" required autoComplete="current-password" value={form.currentPassword}
          onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="pwd-new">Nouveau mot de passe</Label>
        <PasswordInput id="pwd-new" required minLength={10} autoComplete="new-password" value={form.newPassword}
          aria-describedby="pwd-rules" onChange={(e) => setForm({ ...form, newPassword: e.target.value })} />
        <p id="pwd-rules" className="text-xs text-muted-foreground">
          10 caractères minimum, avec majuscule, minuscule, chiffre et caractère spécial.
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="pwd-confirm">Confirmer le nouveau mot de passe</Label>
        <PasswordInput id="pwd-confirm" required autoComplete="new-password" value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        {mismatch && <p role="alert" className="text-sm text-danger">Les deux mots de passe ne correspondent pas</p>}
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={change.isPending}>{change.isPending ? 'Modification…' : 'Changer le mot de passe'}</Button>
        <Button type="button" variant="outline" onClick={() => { setForm(EMPTY); setMismatch(false); onDone(); }}>Annuler</Button>
      </div>
    </form>
  );
}
